import { axiosInstance } from './axiosConfig.ts';
import { handleRequestWithAuth } from './refreshService.ts';

export const ADMINISTRATIVE_REPORT_TYPES = [
  'cafeteria', 'cancelaciones', 'reservas', 'pagos', 'caja', 'uso', 'clientes', 'actividad', 'seguridad',
] as const;

export type AdministrativeReportType = typeof ADMINISTRATIVE_REPORT_TYPES[number];
export type AdministrativeReportColumnType = 'text' | 'number' | 'currency' | 'money' | 'date' | 'datetime' | 'boolean';

export interface AdministrativeReportColumn {
  key: string;
  label: string;
  type?: AdministrativeReportColumnType;
  aggregate?: 'sum';
}

export interface AdministrativeReportGroupOption {
  key: string;
  label: string;
}

export interface AdministrativeReportResponse {
  columns: AdministrativeReportColumn[];
  rows: Record<string, unknown>[];
  summary: Record<string, unknown>;
  group_options: AdministrativeReportGroupOption[];
}

export type AdministrativeReportFilters = Record<string, string | number | boolean | undefined | null>;

export interface AdministrativeReportFilterOption {
  id: number;
  label: string;
  detail: string;
}

export interface AdministrativeReportFilterOptions {
  clientes: AdministrativeReportFilterOption[];
  sucursales: AdministrativeReportFilterOption[];
  servicios: AdministrativeReportFilterOption[];
  espacios: AdministrativeReportFilterOption[];
  tipos_pago: AdministrativeReportFilterOption[];
  usuarios: AdministrativeReportFilterOption[];
}

const OPTION_KEYS: (keyof AdministrativeReportFilterOptions)[] = [
  'clientes', 'sucursales', 'servicios', 'espacios', 'tipos_pago', 'usuarios',
];

const COLUMN_TYPES = new Set<AdministrativeReportColumnType>([
  'text', 'number', 'currency', 'money', 'date', 'datetime', 'boolean',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isColumn = (value: unknown): value is AdministrativeReportColumn => {
  if (!isRecord(value) || typeof value.key !== 'string' || !value.key.trim()
    || typeof value.label !== 'string' || !value.label.trim()) return false;
  if (value.type !== undefined && (typeof value.type !== 'string' || !COLUMN_TYPES.has(value.type as AdministrativeReportColumnType))) return false;
  return value.aggregate === undefined || value.aggregate === 'sum';
};

const isGroupOption = (value: unknown): value is AdministrativeReportGroupOption =>
  isRecord(value) && typeof value.key === 'string' && Boolean(value.key.trim())
  && typeof value.label === 'string' && Boolean(value.label.trim());

const isFilterOption = (value: unknown): value is AdministrativeReportFilterOption =>
  isRecord(value) && Number.isInteger(value.id) && Number(value.id) > 0
  && typeof value.label === 'string' && Boolean(value.label.trim())
  && typeof value.detail === 'string';

export const fetchAdministrativeReportOptions = async (): Promise<AdministrativeReportFilterOptions> =>
  handleRequestWithAuth(async () => {
    const response = await axiosInstance.get<unknown>('reportes/opciones/');
    const data = response.data;
    const valid = isRecord(data) && OPTION_KEYS.every(key => {
      const collection = data[key];
      return Array.isArray(collection) && collection.every(isFilterOption);
    });
    if (!valid) {
      throw new Error('Respuesta inválida de opciones de reportes.');
    }
    return data as unknown as AdministrativeReportFilterOptions;
  });

export const fetchAdministrativeReport = async (
  type: AdministrativeReportType,
  filters: AdministrativeReportFilters = {},
): Promise<AdministrativeReportResponse> => handleRequestWithAuth(async () => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined && value !== null),
  );
  const response = await axiosInstance.get<AdministrativeReportResponse>(`reportes/${type}/`, { params });
  const data: unknown = response.data;
  if (!isRecord(data)) throw new Error('Respuesta inválida del servicio de reportes.');
  const validColumns = Array.isArray(data.columns) && data.columns.every(isColumn);
  const validRows = Array.isArray(data.rows) && data.rows.every(isRecord);
  const validSummary = isRecord(data.summary);
  const validGroups = Array.isArray(data.group_options) && data.group_options.every(isGroupOption);
  if (!validColumns || !validRows || !validSummary || !validGroups) {
    throw new Error('Respuesta inválida del servicio de reportes.');
  }
  return data as unknown as AdministrativeReportResponse;
});
